/* ============================================================
 * RVRN Product Grid — Section JS
 * 商品数据源：section settings.collection（与 filter 共用）。
 *
 * 筛选维度绑定「商品 metafield」（如 custom.series），采用客户端本地过滤：
 * 首屏渲染 collection 全量卡片 + [data-grid-meta]（含各维度 metafield 值）。
 * 按 同维度 OR / 跨维度 AND，用 delimiter 拆分 metafield 多值匹配。
 *
 * 分页：根据后台 Items per page（与 Columns 决定布局）客户端分页显示。
 *   页码 / 每页数量持久化在 URL（?rvrn_page=N&rvrn_pp=M，见 _pushURL / _restoreFromURL），
 *   点商品再后退、分享链接回来都不会丢页。
 *
 * 事件：rvrn:collection-filter-change
 *   detail: { collection, delimiter, metaKeys[], selByDim[], tags[], count, hasSelection, init }
 *   init=true 表示筛选端的初始化回显（URL 恢复），本节不会把页码重置回第 1 页。
 * ============================================================ */

(function () {
  'use strict';

  const SECTION_CLASS = 'rvrn-product-grid';
  const EVENT_NAME = 'rvrn:collection-filter-change';

  // 分页状态写进 URL，离开本页再回来（点商品 → 后退、分享链接）都能恢复到原来那一页。
  // 刻意避开 Shopify 原生 ?page= —— 本节 Liquid 用 {% paginate collection.products by 250 %}，
  // 那个参数属于服务端分页，直接借用会把首屏渲染也一起翻页。
  const PAGE_PARAM = 'rvrn_page';
  const PER_PAGE_PARAM = 'rvrn_pp';

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.' + SECTION_CLASS).forEach(function (el) {
      if (el.dataset.initialized) return;
      el.dataset.initialized = '1';
      try { new RVRNProductGrid(el); }
      catch (e) { console.error('RVRN Product Grid init failed:', e); }
    });
  });

  function RVRNProductGrid(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || root.id.replace('shopify-section-', '');
    this.collectionHandle = root.dataset.collection || '';
    this.delimiter = (root.dataset.tagDelimiter || '|').trim() || '|';
    this.perPage = parseInt(root.dataset.perPage, 10) || 12;
    this.columns = parseInt(root.dataset.columns, 10) || 4;

    this.sortSource = '';   // 'price' 或 'meta:<namespace.key>'
    this.sortDirection = '';
    this.lastFilterDetail = { hasSelection: false, selByDim: [], metaKeys: [] };

    this.gridEl = root.querySelector('[data-grid]');
    this.statusEl = root.querySelector('[data-status]');
    this.emptyEl = root.querySelector('[data-empty]');
    this.pagerEl = root.querySelector('[data-pagination]');
    this.sortSelect = root.querySelector('[data-sort-select]');
    this.perPageSelect = root.querySelector('[data-per-page-select]');

    this.cards = Array.from(this.gridEl.querySelectorAll('.rvrn-wheel-card-v2'));
    const metaNodes = Array.from(this.gridEl.querySelectorAll('[data-grid-meta]'));
    this.cardMeta = this.cards.map(function (card, i) {
      const node = metaNodes[i];
      if (!node) return null;
      try { return JSON.parse(node.textContent || node.innerHTML || ''); }
      catch (e) { return null; }
    });

    this.currentPage = 1;
    this.filteredCards = this.cards.slice(); // 当前筛选后可见卡片
    this.defaultPerPage = this.perPage;      // 后台默认每页数量（用于判断 URL 里的 pp 是否非默认）

    this._syncCardLinks();          // 卡片链接带上当前页面的查询参数（?filter=… 等）
    this._bindFilterEvent();
    this._bindControls();
    this._restoreFromURL();         // 恢复上次的页码 / 每页数量（必须早于首次渲染）
    this._renderPage();
    this._setStatusShowingAll(this.cards.length);
  }

  /**
   * 给卡片链接补上当前页面的查询参数。
   *
   * 为什么必须做（两点缺一不可）：
   *  1) `product.url | within: collection` 只补「路径」上下文
   *     （/collections/<handle>/products/<handle>），不携带任何 query；
   *  2) 本节列表的筛选/排序状态只存在于 URL 里 ——
   *     assets/section-rvrn-collection-filter.js#_pushURL 用 history.replaceState
   *     把选择写成 ?filter=<tags>，集合跳转还用 ?view=rvrn-grid&constraint=tag:…&page=N。
   *     而卡片是首屏全量渲染 + 客户端过滤，链接不带这些参数时，
   *     点进商品（或再返回集合）就会把筛选状态丢掉。
   *
   * 触发时机：初始化一次 + 每次筛选变化（rvrn:collection-filter-change）。
   * 筛选是客户端过滤、不刷新页面，所以必须在事件里重新同步，否则链接会停在旧参数上。
   * 幂等：原始地址记在 data-rvrn-base-href，反复调用不会叠加参数。
   */
  RVRNProductGrid.prototype._syncCardLinks = function () {
    if (!this.gridEl) return;
    const params = new URLSearchParams(window.location.search);
    // 商品页/埋点专用参数不该带到集合链接上（?variant= 会指向当前商品的具体款式）
    ['variant', '_pos', '_fid', '_ss', '_psq'].forEach(function (k) { params.delete(k); });
    const qs = params.toString();
    // 查询串没变就不重写（一次翻页最多 250 张卡片，逐张 setAttribute 纯属浪费；
    // 现在翻页后也会调用本方法，见 _renderPage）。
    if (this._lastSyncedQS === qs) return;
    this._lastSyncedQS = qs;
    this.gridEl.querySelectorAll('a.rvrn-wheel-card-v2__link, a.rvrn-configurator-strip').forEach(function (a) {
      const base = a.getAttribute('data-rvrn-base-href') || a.getAttribute('href') || '';
      if (!base || base.charAt(0) === '#' || /^https?:/i.test(base)) return;
      if (!a.hasAttribute('data-rvrn-base-href')) a.setAttribute('data-rvrn-base-href', base);
      a.setAttribute('href', qs ? base + '?' + qs : base);
    });
  };

  RVRNProductGrid.prototype._bindFilterEvent = function () {
    const self = this;
    document.addEventListener(EVENT_NAME, function (e) {
      const detail = e.detail || {};
      if (self.collectionHandle && detail.collection && detail.collection !== self.collectionHandle) return;
      self._applyFilter(detail);
      self._syncCardLinks();        // 筛选变化后（URL 已被 replaceState 更新）重新同步链接
    });
  };

  RVRNProductGrid.prototype._bindControls = function () {
    const self = this;
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        // 选项值格式: "price:asc" | "meta:<namespace.key>:asc" | "" (默认)
        // 注意 meta 排序含两段冒号，用 lastIndexOf 切分方向，避免把 namespace.key 误判为方向。
        const v = self.sortSelect.value || '';
        if (!v) {
          self.sortSource = '';
          self.sortDirection = '';
        } else {
          const idx = v.lastIndexOf(':');
          self.sortDirection = v.slice(idx + 1);
          self.sortSource = v.slice(0, idx);
        }
        // 用户主动改排序 → 视为主动操作，去掉 init 标记，重新从第 1 页看起
        const detail = Object.assign({}, self.lastFilterDetail);
        delete detail.init;
        self._applyFilter(detail);
      });
    }
    if (this.perPageSelect) {
      this.perPageSelect.addEventListener('change', function () {
        const v = parseInt(self.perPageSelect.value, 10);
        if (!isNaN(v) && v > 0) {
          self.perPage = v;
          self.currentPage = 1;
          self._renderPage();
          self._scrollToGridTop();      // 每页数量变化 → 回到第 1 页，同样回到第一行
        }
      });
    }
  };

  RVRNProductGrid.prototype._splitValues = function (raw) {
    if (!raw) return [];
    return String(raw).split(this.delimiter).map(function (s) { return s.trim(); }).filter(Boolean);
  };

  // 把 object / array / string 等多类型 metafield 值展平为可比较的字符串数组。
  // 与 filter 端一致：object 优先取 name / title / label（如 custom.construction = {name:'1-Piece'}）。
  // 否则 grid 端只会得到 "[object Object]"，与 filter 传入的 '1-Piece' 永远无法匹配 → 筛后无数据。
  RVRNProductGrid.prototype._flattenMetaValue = function (raw) {
    if (raw === null || raw === undefined) return [];
    let vals = [];
    if (typeof raw === 'string') {
      vals = this._splitValues(raw);
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      vals = [String(raw)];
    } else if (Array.isArray(raw)) {
      const out = [];
      raw.forEach(function (item) { self_flatten(out, item); });
      vals = dedupe(out);
    } else if (typeof raw === 'object') {
      const named = raw.name != null ? raw.name : (raw.title != null ? raw.title : raw.label);
      if (named != null) {
        vals = this._splitValues(String(named));
      } else {
        const out = [];
        Object.keys(raw).forEach(function (k) { self_flatten(out, raw[k]); });
        vals = dedupe(out);
      }
    }
    return vals;
  };

  RVRNProductGrid.prototype._matchDimension = function (meta, metaKey, selectedTags) {
    if (!selectedTags || selectedTags.length === 0) return true;
    const raw = meta && meta.mf ? meta.mf[metaKey] : null;
    const values = this._flattenMetaValue(raw);
    return selectedTags.some(function (t) { return values.indexOf(t) > -1; });
  };

  RVRNProductGrid.prototype._applyFilter = function (detail) {
    detail = detail || {};
    this.lastFilterDetail = detail;
    const selByDim = detail.selByDim || [];
    const metaKeys = detail.metaKeys || [];
    const hasSelection = detail.hasSelection;
    if (detail.delimiter) this.delimiter = detail.delimiter;

    const self = this;
    this.filteredCards = this.cards.filter(function (card) {
      const idx = self.cards.indexOf(card);
      const meta = self.cardMeta[idx];
      if (!hasSelection || !meta) return true;
      for (let i = 0; i < metaKeys.length; i++) {
        const sel = selByDim[i] || [];
        if (sel.length === 0) continue;
        if (!self._matchDimension(meta, metaKeys[i], sel)) return false;
      }
      return true;
    });

    this._sortFiltered();
    this._syncDOMOrder();

    // 只有「用户真的改了筛选」才回到第 1 页。
    // detail.init 是筛选端的初始化回显（内容来自 ?filter= 恢复），此时 URL 里的
    // ?rvrn_page 与筛选状态本来就是配套的，重置会把刚恢复出来的页码打掉、退回第一页。
    if (!(detail.init === true)) this.currentPage = 1;

    this.emptyEl.hidden = this.filteredCards.length !== 0;
    this.statusEl.hidden = false;
    this.statusEl.textContent = hasSelection
      ? 'Filtered Results (' + this.filteredCards.length + ')'
      : 'Showing All (' + this.cards.length + ')';
    this._renderPage();
  };

  // 根据当前排序设置对 filteredCards 重新排序
  RVRNProductGrid.prototype._sortFiltered = function () {
    if (!this.sortSource || !this.sortDirection) return;
    const self = this;
    const isPrice = self.sortSource === 'price';
    const metaKey = isPrice ? null : self.sortSource.replace(/^meta:/, '');
    this.filteredCards.sort(function (a, b) {
      const idxA = self.cards.indexOf(a);
      const idxB = self.cards.indexOf(b);
      const metaA = self.cardMeta[idxA];
      const metaB = self.cardMeta[idxB];
      let rawA = null, rawB = null;
      if (isPrice) {
        rawA = a.dataset.price;
        rawB = b.dataset.price;
      } else {
        rawA = metaA && metaA.mf ? metaA.mf[metaKey] : null;
        rawB = metaB && metaB.mf ? metaB.mf[metaKey] : null;
      }
      const valA = self._normalizeSortValue(rawA);
      const valB = self._normalizeSortValue(rawB);
      return self._compareSortValues(valA, valB, self.sortDirection);
    });
  };

  // 把 object / array 等 metafield 值规范化为可比较对象 {type, val}
  RVRNProductGrid.prototype._normalizeSortValue = function (raw) {
    if (raw === null || raw === undefined) return { type: 'empty', val: '' };
    if (typeof raw === 'object') {
      const named = raw.name != null ? raw.name : (raw.title != null ? raw.title : raw.label);
      if (named != null) return this._normalizeSortValue(named);
    }
    const s = String(raw).trim();
    if (s === '') return { type: 'empty', val: '' };
    const n = parseFloat(s);
    if (!isNaN(n) && isFinite(n)) return { type: 'number', val: n };
    return { type: 'string', val: s.toLowerCase() };
  };

  RVRNProductGrid.prototype._compareSortValues = function (a, b, direction) {
    const dir = direction === 'desc' ? -1 : 1;
    // 空值始终排到最后（升序、降序都放末尾）
    if (a.type === 'empty' && b.type === 'empty') return 0;
    if (a.type === 'empty') return 1;
    if (b.type === 'empty') return -1;
    if (a.type === 'number' && b.type === 'number') {
      return (a.val - b.val) * dir;
    }
    return (a.val < b.val ? -1 : (a.val > b.val ? 1 : 0)) * dir;
  };

  // 把筛选/排序后的卡片重新按 filteredCards 顺序插入 DOM，确保视觉顺序与逻辑顺序一致
  // 注意：必须 append 到真正的 CSS grid 容器（.rvrn-collection-grid-items），
  // 而不是其父级 [data-grid]，否则卡片会被移出 grid 容器导致单列堆叠。
  RVRNProductGrid.prototype._syncDOMOrder = function () {
    const grid = this.gridEl ? this.gridEl.querySelector('.rvrn-collection-grid-items') : null;
    if (!grid) return;
    const visibleMap = {};
    this.filteredCards.forEach(function (card) {
      visibleMap[card] = true;
      grid.appendChild(card);
    });
    // 隐藏（被过滤掉）的卡片append到后面，保持原始顺序
    this.cards.forEach(function (card) {
      if (!visibleMap[card]) grid.appendChild(card);
    });
  };

  // 根据当前 filteredCards + currentPage + perPage 显隐卡片并渲染分页器
  RVRNProductGrid.prototype._renderPage = function () {
    const total = this.filteredCards.length;
    const totalPages = Math.max(1, Math.ceil(total / this.perPage));
    if (this.currentPage > totalPages) this.currentPage = totalPages;

    const start = (this.currentPage - 1) * this.perPage;
    const end = start + this.perPage;

    // 先全部隐藏，再显示当前页（filteredCards 之外的保持隐藏）
    this.cards.forEach(function (c) { c.style.display = 'none'; });
    this.filteredCards.forEach(function (c, i) {
      c.style.display = (i >= start && i < end) ? '' : 'none';
    });

    this._renderPagination(totalPages);
    this._pushURL();          // 页码/每页数量写回 URL，离开本页再回来可恢复
    this._syncCardLinks();    // URL 变了 → 卡片链接跟着带上新页号，点进商品再后退才能回到这一页
  };

  /**
   * 从 URL 恢复分页状态（?rvrn_page=N&rvrn_pp=M）。
   *
   * 为什么需要：分页是纯客户端显隐，翻页只改 DOM。一旦离开本页
   * （点商品、切集合，或整页回退）再回来就是全新渲染 —— 不读 URL 就永远停在第一页。
   * 页码由 _pushURL 随每次翻页写入，卡片链接又会带着它去商品页（_syncCardLinks），
   * 所以「点商品 → 浏览器后退」这条路径能把页号带回来。
   *
   * 容错：页码交给 _renderPage 收敛到 [1, totalPages]；每页数量必须是后台
   * per_page_options 里存在的值，否则沿用默认，避免出现下拉框对不上的空档页。
   */
  RVRNProductGrid.prototype._restoreFromURL = function () {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }

    const pp = parseInt(params.get(PER_PAGE_PARAM), 10);
    if (!isNaN(pp) && pp > 0) {
      let allowed = true;
      if (this.perPageSelect) {
        allowed = Array.prototype.some.call(this.perPageSelect.options, function (o) {
          return parseInt(o.value, 10) === pp;
        });
      }
      if (allowed) {
        this.perPage = pp;
        if (this.perPageSelect) this.perPageSelect.value = String(pp);
      }
    }

    const page = parseInt(params.get(PAGE_PARAM), 10);
    if (!isNaN(page) && page > 1) this.currentPage = page;
  };

  /**
   * 把当前分页状态写回 URL。
   *
   * 用 replaceState 而非 pushState：翻页不该产生历史记录，
   * 浏览器的「后退」应当是离开本页，而不是在页 1 / 页 2 之间来回跳。
   *
   * 只写非默认值（第 1 页、默认每页数量就把参数删掉），URL 保持干净；
   * 其余参数（?filter=… 等）原样保留，与 section-rvrn-collection-filter.js#_pushURL
   * 共用同一个查询串，避免两边互相覆盖。
   */
  RVRNProductGrid.prototype._pushURL = function () {
    try {
      const params = new URLSearchParams(window.location.search);
      if (this.currentPage > 1) params.set(PAGE_PARAM, String(this.currentPage));
      else params.delete(PAGE_PARAM);
      if (this.perPage && this.perPage !== this.defaultPerPage) params.set(PER_PAGE_PARAM, String(this.perPage));
      else params.delete(PER_PAGE_PARAM);
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e) { /* ignore */ }
  };

  RVRNProductGrid.prototype._renderPagination = function (totalPages) {
    if (!this.pagerEl) return;
    if (totalPages <= 1) { this.pagerEl.hidden = true; this.pagerEl.innerHTML = ''; return; }
    this.pagerEl.hidden = false;
    const self = this;
    const isFirst = this.currentPage <= 1;
    const isLast = this.currentPage >= totalPages;

    const html =
      '<button type="button" class="rvrn-btn rvrn-btn--control rvrn-product-grid__page rvrn-product-grid__page--prev" data-action="prev"' + (isFirst ? ' disabled' : '') + '>← Prev</button>' +
      '<span class="rvrn-product-grid__page-indicator" aria-live="polite">' + this.currentPage + ' / ' + totalPages + '</span>' +
      '<button type="button" class="rvrn-btn rvrn-btn--control rvrn-product-grid__page rvrn-product-grid__page--next" data-action="next"' + (isLast ? ' disabled' : '') + '>Next →</button>';

    this.pagerEl.innerHTML = html;

    const prevBtn = this.pagerEl.querySelector('[data-action="prev"]');
    const nextBtn = this.pagerEl.querySelector('[data-action="next"]');

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (self.currentPage > 1) {
          self.currentPage -= 1;
          self._renderPage();
          self._scrollToGridTop();
        }
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (self.currentPage < totalPages) {
          self.currentPage += 1;
          self._renderPage();
          self._scrollToGridTop();
        }
      });
    }
  };

  // 回滚时顶部额外留白，卡片不紧贴 header 下沿。
  const SCROLL_TOP_GAP = 16;

  /**
   * 计算「当前贴在视口顶部、会遮住内容」的条带总高度（px）。
   *
   * 为什么必须算：页面滚动后页头是吸顶的（Halo 主题用 jquery.sticky 把
   * [data-sticky-pc] / [data-sticky-mb] 包进 .sticky-wrapper 并加 is-sticky，
   * 同时给 body 加 has_sticky），集合页还可能在页头下面再压一条
   * .toolbar.toolbar-fix。只按网格自身位置回滚，第一行会藏在 header 背后。
   *
   * 做法：从视口顶部往下逐层堆叠 —— 凡是 top 贴住「上一段底部」的条带，
   * 就把它的 bottom 计入遮挡高度（覆盖 公告栏 + 主导航 + 筛选工具栏 叠加的场景）。
   * 已滚出顶部的非吸顶 header（bottom <= 0）自然被排除，返回 0。
   */
  function getTopOverlayOffset() {
    const nodes = document.querySelectorAll(
      '.sticky-wrapper, [data-sticky-pc], [data-sticky-mb], .site-header, .toolbar.toolbar-fix'
    );
    const rects = [];
    Array.prototype.forEach.call(nodes, function (el) {
      rects.push(el.getBoundingClientRect());
    });

    let offset = 0;
    let stacked = true;
    while (stacked) {
      stacked = false;
      rects.forEach(function (r) {
        if (r.height <= 0 || r.bottom <= offset) return;
        if (r.top <= offset + 2) {                 // 紧贴视口顶部或上一段底部
          offset = Math.max(offset, r.bottom);
          stacked = true;                          // offset 变大 → 再扫一轮
        }
      });
    }
    return offset;
  }

  /**
   * 翻页后把网格带回「第一行」。
   *
   * 为什么需要：分页是纯客户端显隐（不重新请求、不刷新页面），
   * 点 Next/Prev 时视口仍停在原来那几行 —— 新一页从网格顶部开始渲染，
   * 用户看到的却是空白或后半段，必须手动滚回第一行。
   *
   * 关键：回滚终点要再减去吸顶 header 的高度（getTopOverlayOffset），
   * 否则滚到「网格顶 = 视口顶」时第一行仍被 header 盖住，看起来还是没回到第一行。
   *
   * 触发时机：点击 Prev/Next、切换 Items per page（回到第 1 页）之后。
   * 不滚动的情况：第一行已在 header 之下可见（top >= offset），保持原位避免无谓跳动。
   */
  RVRNProductGrid.prototype._scrollToGridTop = function () {
    const self = this;
    if (!self.gridEl) return;
    // 放到下一帧执行：卡片显隐刚变更，等布局稳定后再测量位置。
    requestAnimationFrame(function () {
      const offset = getTopOverlayOffset() + SCROLL_TOP_GAP;
      const top = self.gridEl.getBoundingClientRect().top;
      if (top >= offset) return;                  // 第一行已在 header 之下，无需滚动
      const targetY = Math.max(0, top + window.pageYOffset - offset);
      try {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, targetY);              // 不支持 options 对象的旧浏览器
      }
    });
  };

  RVRNProductGrid.prototype._setStatusShowingAll = function (n) {
    if (!this.statusEl) return;
    this.statusEl.hidden = false;
    this.statusEl.textContent = 'Showing All (' + n + ')';
  };

  // 内部辅助：递归把单个值压平追加到 out（与 filter 端一致）
  function self_flatten(out, val) {
    if (val === null || val === undefined) return;
    if (typeof val === 'string') {
      val.split('|').map(function (s) { return s.trim(); }).filter(Boolean).forEach(function (s) { out.push(s); });
    } else if (typeof val === 'number' || typeof val === 'boolean') {
      out.push(String(val));
    } else if (Array.isArray(val)) {
      val.forEach(function (item) { self_flatten(out, item); });
    } else if (typeof val === 'object') {
      // metaobject / object：优先只取展示字段 name/title/label，忽略 active/display_order 等噪声字段
      var named = val.name != null ? val.name
        : (val.title != null ? val.title
        : (val.label != null ? val.label : null));
      if (named != null) {
        self_flatten(out, named);
      } else {
        Object.keys(val).forEach(function (k) { self_flatten(out, val[k]); });
      }
    }
  }

  function dedupe(arr) {
    const seen = {};
    const out = [];
    arr.forEach(function (v) {
      if (!seen[v]) { seen[v] = true; out.push(v); }
    });
    return out;
  }

})();
